import {
  addDays,
  createBaseRecord,
  ENTITY_TYPES,
  entityArrayKey,
  fromWireEntity,
  installmentAmounts,
  remainingAmount,
  recurrenceOccurrenceID,
  recurrenceOccurrences,
  todayLocalDay,
  toWireEntity,
  validateLedger,
  type AnyEntity,
  type EntityMap,
  type EntityType,
  type LedgerSnapshot,
  type MutationGroup,
  type SyncOperation,
  type WireEntity
} from '@kemicontrol/domain';
import { db, entityKey, getEntity, snapshotForUser, type StoredEntity, type StoredMutationGroup } from './db';

export interface LocalOperation<T extends EntityType = EntityType> {
  entityType: T;
  action: 'upsert' | 'delete';
  record: EntityMap[T];
}

function applyToSnapshot(snapshot: LedgerSnapshot, operation: LocalOperation): void {
  if (operation.entityType === 'appSettings') {
    snapshot.settings = operation.record as EntityMap['appSettings'];
    return;
  }
  const key = entityArrayKey[operation.entityType] as Exclude<keyof LedgerSnapshot, 'settings'>;
  const records = snapshot[key] as AnyEntity[];
  const index = records.findIndex((record) => record.id === operation.record.id);
  if (index >= 0) records[index] = operation.record;
  else records.push(operation.record);
}

function recordsFor(snapshot: LedgerSnapshot, type: EntityType): AnyEntity[] {
  return type === 'appSettings' ? [snapshot.settings] : snapshot[entityArrayKey[type]] as AnyEntity[];
}

function contentSignature(type: EntityType, record: AnyEntity): string {
  const wire = { ...toWireEntity(type, record as never) };
  delete wire.version;
  delete wire.updatedAt;
  return JSON.stringify(Object.fromEntries(Object.entries(wire).sort(([left], [right]) => left.localeCompare(right))));
}

function snapshotFromRecords(records: Map<EntityType, AnyEntity[]>): LedgerSnapshot {
  const settings = records.get('appSettings')?.[0] as EntityMap['appSettings'] | undefined;
  if (!settings) throw new Error('Backup sem ajustes.');
  return {
    accounts: records.get('account') as EntityMap['account'][] ?? [],
    incomeSources: records.get('incomeSource') as EntityMap['incomeSource'][] ?? [],
    categories: records.get('category') as EntityMap['category'][] ?? [],
    incomes: records.get('income') as EntityMap['income'][] ?? [],
    expenses: records.get('expense') as EntityMap['expense'][] ?? [],
    payments: records.get('payment') as EntityMap['payment'][] ?? [],
    transfers: records.get('transfer') as EntityMap['transfer'][] ?? [],
    adjustments: records.get('adjustment') as EntityMap['adjustment'][] ?? [],
    recurrenceRules: records.get('recurrenceRule') as EntityMap['recurrenceRule'][] ?? [],
    recurrenceExceptions: records.get('recurrenceException') as EntityMap['recurrenceException'][] ?? [],
    settings
  };
}

export async function commitLocalMutation(userId: string, operations: LocalOperation[]): Promise<string> {
  if (!operations.length || operations.length > 20) throw new Error('Grupo de alterações inválido.');
  const now = new Date().toISOString();
  const normalized = await Promise.all(operations.map(async (operation) => {
    const current = await getEntity(userId, operation.entityType, operation.record.id);
    const baseVersion = current?.version ?? 0;
    const record = { ...operation.record, updatedAt: now, deletedAt: operation.action === 'delete' ? now : operation.record.deletedAt } as AnyEntity;
    return { ...operation, record, baseVersion };
  }));
  const snapshot = await snapshotForUser(userId);
  for (const operation of normalized) applyToSnapshot(snapshot, operation as LocalOperation);
  validateLedger(snapshot);

  const mutationId = crypto.randomUUID();
  const syncOperations: SyncOperation[] = normalized.map((operation) => ({
    entityType: operation.entityType,
    entityId: operation.record.id,
    baseVersion: operation.baseVersion,
    action: operation.action,
    record: toWireEntity(operation.entityType, operation.record as any)
  }));
  const stored: StoredMutationGroup = { mutationId, operations: syncOperations, userId, createdAt: now, attempts: 0 };
  const entities: StoredEntity[] = normalized.map((operation) => ({ key: entityKey(userId, operation.entityType, operation.record.id), userId, type: operation.entityType, record: operation.record }));

  await db.transaction('rw', db.entities, db.outbox, db.syncState, async () => {
    await db.entities.bulkPut(entities);
    await db.outbox.put(stored);
    const state = await db.syncState.get(userId);
    await db.syncState.put({ userId, lastRevision: state?.lastRevision ?? 0, status: navigator.onLine ? 'saved-local' : 'offline', lastSyncAt: state?.lastSyncAt });
  });
  return mutationId;
}

function dependencyReferences(type: EntityType, record: AnyEntity): Array<{ type: EntityType; id: string }> {
  const value = record as unknown as Record<string, unknown>;
  const reference = (dependencyType: EntityType, field: string) => typeof value[field] === 'string' ? [{ type: dependencyType, id: value[field] as string }] : [];
  switch (type) {
    case 'category': return reference('category', 'parentID');
    case 'income': return [...reference('account', 'accountID'), ...reference('incomeSource', 'sourceID')];
    case 'expense': return [...reference('category', 'categoryID'), ...reference('recurrenceRule', 'recurrenceRuleID')];
    case 'payment': return [...reference('account', 'accountID'), ...reference('expense', 'expenseID')];
    case 'transfer': return [...reference('account', 'sourceAccountID'), ...reference('account', 'destinationAccountID')];
    case 'adjustment': return reference('account', 'accountID');
    case 'recurrenceRule': return reference('category', 'categoryID');
    case 'recurrenceException': return reference('recurrenceRule', 'recurrenceRuleID');
    default: return [];
  }
}

export async function restoreEntityWithDependencies(userId: string, type: EntityType, id: string): Promise<number> {
  const snapshot = await snapshotForUser(userId);
  const operations: LocalOperation[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();

  function visit(currentType: EntityType, currentId: string) {
    const key = `${currentType}:${currentId}`;
    if (visited.has(key)) return;
    if (visiting.has(key)) throw new Error('Não foi possível restaurar: dependências circulares.');
    const record = recordsFor(snapshot, currentType).find((candidate) => candidate.id === currentId);
    if (!record) throw new Error(`Não foi possível restaurar: dependência ${currentType} não encontrada.`);
    visiting.add(key);
    for (const dependency of dependencyReferences(currentType, record)) visit(dependency.type, dependency.id);
    visiting.delete(key);
    visited.add(key);
    if (!record.deletedAt) return;
    const { deletedAt: _deletedAt, ...restored } = record;
    operations.push({ entityType: currentType, action: 'upsert', record: restored } as LocalOperation);
  }

  visit(type, id);
  if (!operations.length) return 0;
  if (operations.length > 20) throw new Error('Não foi possível restaurar: o grupo de dependências é grande demais.');
  await commitLocalMutation(userId, operations);
  return operations.length;
}

export async function applyCanonicalOperations(userId: string, operations: { entityType: EntityType; entityId: string; record: WireEntity }[]): Promise<void> {
  const values = operations.map((operation) => {
    const record = fromWireEntity(operation.entityType, operation.record) as AnyEntity;
    return { key: entityKey(userId, operation.entityType, operation.entityId), userId, type: operation.entityType, record } satisfies StoredEntity;
  });
  await db.entities.bulkPut(values);
}

export async function markMutationApplied(userId: string, group: MutationGroup, revision: number, operations: { entityType: EntityType; entityId: string; record: WireEntity }[]): Promise<void> {
  await db.transaction('rw', db.entities, db.outbox, db.syncState, async () => {
    await applyCanonicalOperations(userId, operations);
    await db.outbox.delete(group.mutationId);
    const state = await db.syncState.get(userId);
    await db.syncState.put({ userId, lastRevision: Math.max(state?.lastRevision ?? 0, revision), status: 'syncing', lastSyncAt: state?.lastSyncAt });
  });
}

export async function mergeClassification(userId: string, type: 'category' | 'incomeSource', sourceId: string, targetId: string): Promise<void> {
  if (sourceId === targetId) throw new Error('Escolha dois itens diferentes.');
  const snapshot = await snapshotForUser(userId);
  const operations: LocalOperation[] = [];
  if (type === 'incomeSource') {
    const source = snapshot.incomeSources.find((item) => item.id === sourceId);
    const target = snapshot.incomeSources.find((item) => item.id === targetId);
    if (!source || !target) throw new Error('Origem não encontrada.');
    operations.push({ entityType: 'incomeSource', action: 'upsert', record: { ...source, isArchived: true, mergedIntoID: targetId } });
    for (const income of snapshot.incomes.filter((item) => item.sourceID === sourceId && !item.deletedAt)) operations.push({ entityType: 'income', action: 'upsert', record: { ...income, sourceID: targetId } });
  } else {
    const source = snapshot.categories.find((item) => item.id === sourceId);
    const target = snapshot.categories.find((item) => item.id === targetId);
    if (!source || !target || source.purpose !== target.purpose || Boolean(source.parentID) !== Boolean(target.parentID)) throw new Error('As categorias devem ter a mesma finalidade e nível.');
    operations.push({ entityType: 'category', action: 'upsert', record: { ...source, isArchived: true, mergedIntoID: targetId } });
    for (const expense of snapshot.expenses.filter((item) => item.categoryID === sourceId && !item.deletedAt)) operations.push({ entityType: 'expense', action: 'upsert', record: { ...expense, categoryID: targetId } });
    for (const rule of snapshot.recurrenceRules.filter((item) => item.categoryID === sourceId && !item.deletedAt)) operations.push({ entityType: 'recurrenceRule', action: 'upsert', record: { ...rule, categoryID: targetId } });
  }
  await db.safetySnapshots.put({ id: crypto.randomUUID(), userId, createdAt: new Date().toISOString(), reason: `pre-merge:${type}:${sourceId}:${targetId}`, snapshot });
  for (let index = 0; index < operations.length; index += 20) await commitLocalMutation(userId, operations.slice(index, index + 20));
}

export async function ensureRecurringExpenses(userId: string): Promise<number> {
  const snapshot = await snapshotForUser(userId);
  const existingKeys = new Set(snapshot.expenses.flatMap((expense) => expense.logicalOccurrenceKey ? [expense.logicalOccurrenceKey] : []));
  const horizon = addDays(todayLocalDay(), 365);
  const operations: LocalOperation<'expense'>[] = [];
  for (const rule of snapshot.recurrenceRules.filter((candidate) => !candidate.deletedAt && candidate.state === 'active')) {
    const days = recurrenceOccurrences(rule, horizon);
    const portions = rule.kind === 'installment' && rule.occurrenceCount ? installmentAmounts(rule.amount, rule.occurrenceCount) : [];
    for (const [index, day] of days.entries()) {
      const logicalOccurrenceKey = `${rule.id}:${day}`;
      if (existingKeys.has(logicalOccurrenceKey)) continue;
      const excluded = snapshot.recurrenceExceptions.some((exception) => !exception.deletedAt && exception.recurrenceRuleID === rule.id && exception.startsOn <= day && exception.endsOn >= day);
      if (excluded) continue;
      const base = createBaseRecord();
      operations.push({
        entityType: 'expense',
        action: 'upsert',
        record: {
          ...base,
          id: await recurrenceOccurrenceID(rule.id, day),
          amount: rule.kind === 'installment' ? portions[index]! : rule.amount,
          purpose: rule.purpose,
          categoryID: rule.categoryID,
          merchant: rule.merchant,
          purchasedOn: day,
          dueOn: day,
          recurrenceRuleID: rule.id,
          logicalOccurrenceKey,
          note: rule.note
        }
      });
      existingKeys.add(logicalOccurrenceKey);
    }
  }
  for (let index = 0; index < operations.length; index += 20) await commitLocalMutation(userId, operations.slice(index, index + 20));
  return operations.length;
}

export async function settleInstallmentSeries(userId: string, ruleID: string, accountID: string): Promise<{ payments: number; amount: bigint }> {
  let snapshot = await snapshotForUser(userId);
  const rule = snapshot.recurrenceRules.find((candidate) => candidate.id === ruleID && !candidate.deletedAt);
  if (!rule || rule.kind !== 'installment' || !rule.occurrenceCount) throw new Error('Parcelamento não encontrado.');
  if (!snapshot.accounts.some((account) => account.id === accountID && !account.deletedAt && !account.isArchived)) throw new Error('Escolha uma conta ativa.');
  const portions = installmentAmounts(rule.amount, rule.occurrenceCount);
  const existingKeys = new Set(snapshot.expenses.flatMap((expense) => expense.logicalOccurrenceKey ? [expense.logicalOccurrenceKey] : []));
  const expenseOperations: LocalOperation<'expense'>[] = [];
  for (const [index, day] of recurrenceOccurrences(rule, '9999-12-31').entries()) {
    const logicalOccurrenceKey = `${rule.id}:${day}`;
    if (existingKeys.has(logicalOccurrenceKey)) continue;
    const excluded = snapshot.recurrenceExceptions.some((exception) => !exception.deletedAt && exception.recurrenceRuleID === rule.id && exception.startsOn <= day && exception.endsOn >= day);
    if (excluded) continue;
    expenseOperations.push({ entityType: 'expense', action: 'upsert', record: { ...createBaseRecord(), id: await recurrenceOccurrenceID(rule.id, day), amount: portions[index]!, purpose: rule.purpose, categoryID: rule.categoryID, merchant: rule.merchant, purchasedOn: day, dueOn: day, recurrenceRuleID: rule.id, logicalOccurrenceKey, note: rule.note } });
  }
  for (let index = 0; index < expenseOperations.length; index += 20) await commitLocalMutation(userId, expenseOperations.slice(index, index + 20));
  snapshot = await snapshotForUser(userId);
  const now = new Date().toISOString();
  const paymentOperations = snapshot.expenses.filter((expense) => expense.recurrenceRuleID === rule.id && !expense.deletedAt && !expense.canceledAt).flatMap((expense) => {
    const amount = remainingAmount(expense, snapshot.payments);
    return amount > 0n ? [{ entityType: 'payment' as const, action: 'upsert' as const, record: { ...createBaseRecord(now), expenseID: expense.id, accountID, amount, paidAt: now } }] : [];
  });
  for (let index = 0; index < paymentOperations.length; index += 20) await commitLocalMutation(userId, paymentOperations.slice(index, index + 20));
  const currentRule = (await snapshotForUser(userId)).recurrenceRules.find((candidate) => candidate.id === rule.id)!;
  await commitLocalMutation(userId, [{ entityType: 'recurrenceRule', action: 'upsert', record: { ...currentRule, state: 'completed', endedOn: todayLocalDay() } }]);
  return { payments: paymentOperations.length, amount: paymentOperations.reduce((total, operation) => total + operation.record.amount, 0n) };
}

export async function restoreLedgerSnapshot(userId: string, target: LedgerSnapshot): Promise<void> {
  validateLedger(target);
  const current = await snapshotForUser(userId);
  const now = new Date().toISOString();
  const finalRecords = new Map<EntityType, AnyEntity[]>();
  const operations: LocalOperation[] = [];

  for (const type of ENTITY_TYPES) {
    const currentById = new Map(recordsFor(current, type).map((record) => [record.id, record]));
    const restored: AnyEntity[] = [];
    for (const candidate of recordsFor(target, type)) {
      const existing = currentById.get(candidate.id);
      currentById.delete(candidate.id);
      if (existing && contentSignature(type, existing) === contentSignature(type, candidate)) {
        restored.push(existing);
        continue;
      }
      const action = candidate.deletedAt ? 'delete' : 'upsert';
      const record = { ...candidate, version: existing?.version ?? 0, updatedAt: now, ...(action === 'delete' ? { deletedAt: now } : { deletedAt: undefined }) } as AnyEntity;
      restored.push(record);
      operations.push({ entityType: type, action, record } as LocalOperation);
    }
    for (const existing of currentById.values()) {
      if (existing.deletedAt || type === 'appSettings') continue;
      const record = { ...existing, updatedAt: now, deletedAt: now } as AnyEntity;
      restored.push(record);
      operations.push({ entityType: type, action: 'delete', record } as LocalOperation);
    }
    finalRecords.set(type, restored);
  }

  const restoredSnapshot = snapshotFromRecords(finalRecords);
  validateLedger(restoredSnapshot);
  const upsertOrder: EntityType[] = ['account', 'incomeSource', 'category', 'appSettings', 'recurrenceRule', 'expense', 'income', 'payment', 'transfer', 'adjustment', 'recurrenceException'];
  const deleteOrder: EntityType[] = ['recurrenceException', 'payment', 'income', 'expense', 'transfer', 'adjustment', 'recurrenceRule', 'category', 'incomeSource', 'account', 'appSettings'];
  operations.sort((left, right) => {
    if (left.action !== right.action) return left.action === 'upsert' ? -1 : 1;
    const order = left.action === 'upsert' ? upsertOrder : deleteOrder;
    return order.indexOf(left.entityType) - order.indexOf(right.entityType);
  });

  const groups: StoredMutationGroup[] = [];
  for (let index = 0; index < operations.length; index += 20) {
    const createdAt = new Date(Date.now() + groups.length).toISOString();
    groups.push({
      mutationId: crypto.randomUUID(),
      userId,
      createdAt,
      attempts: 0,
      operations: operations.slice(index, index + 20).map((operation) => ({
        entityType: operation.entityType,
        entityId: operation.record.id,
        baseVersion: recordsFor(current, operation.entityType).find((record) => record.id === operation.record.id)?.version ?? 0,
        action: operation.action,
        record: toWireEntity(operation.entityType, operation.record as never)
      }))
    });
  }
  const entities = [...finalRecords.entries()].flatMap(([type, records]) => records.map((record) => ({ key: entityKey(userId, type, record.id), userId, type, record } satisfies StoredEntity)));
  await db.transaction('rw', db.entities, db.outbox, db.conflicts, db.syncState, db.safetySnapshots, async () => {
    await db.safetySnapshots.put({ id: crypto.randomUUID(), userId, createdAt: now, reason: 'pre-restore', snapshot: current });
    await db.entities.where('userId').equals(userId).delete();
    await db.entities.bulkPut(entities);
    await db.outbox.where('userId').equals(userId).delete();
    await db.conflicts.where('userId').equals(userId).delete();
    if (groups.length) await db.outbox.bulkPut(groups);
    const state = await db.syncState.get(userId);
    await db.syncState.put({ userId, lastRevision: state?.lastRevision ?? 0, status: groups.length ? (navigator.onLine ? 'saved-local' : 'offline') : 'synced', lastSyncAt: state?.lastSyncAt });
  });
}

export function decodeStoredOperation(operation: SyncOperation): AnyEntity | undefined {
  return operation.record ? fromWireEntity(operation.entityType, operation.record) as AnyEntity : undefined;
}

export function supportedEntityTypes(): readonly EntityType[] { return ENTITY_TYPES; }

export async function resolveConflict(conflictId: string, choice: 'local' | 'remote'): Promise<void> {
  const conflict = await db.conflicts.get(conflictId);
  if (!conflict) return;
  const remote: StoredEntity = { key: entityKey(conflict.userId, conflict.entityType, conflict.entityId), userId: conflict.userId, type: conflict.entityType, record: conflict.remoteRecord };
  await db.transaction('rw', db.entities, db.outbox, db.conflicts, async () => {
    await db.entities.put(remote);
    await db.outbox.delete(conflict.mutationId);
    await db.conflicts.delete(conflict.id);
  });
  if (choice === 'local' && conflict.localRecord) {
    await commitLocalMutation(conflict.userId, [{ entityType: conflict.entityType, action: conflict.localRecord.deletedAt ? 'delete' : 'upsert', record: conflict.localRecord } as LocalOperation]);
  }
}
